import { useCallback, Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectPickedSpecialists } from '../../../selectors/selectPickedSpecialist';
import { TAnswersState } from '../../FormContainer/types/internal';
import { IGeoResultValue } from '../../../actions/submitForm/types';
import { trackMaxRealtors, trackSelectRealtor, trackSendForm, trackUnselectRealtor } from './tracking';
import { specialistsApi } from '../../../api/specialistsApi';
import { addSpecialist, removeSpecialist } from '../../../slices/realtorsSlice';
import { ISpecialistCatalogListing } from '../../../types/demand/specialistListing';
import { EAnswerField } from '../../../types/form/enums';

interface IUseRealtorListingParams {
  answerField: EAnswerField;
  setNextStep: (answersCollection: TAnswersState) => void;
  setAnswersState: Dispatch<SetStateAction<TAnswersState>>;
  geo: IGeoResultValue;
}

export const useRealtorListing = ({ answerField, setNextStep, setAnswersState, geo }: IUseRealtorListingParams) => {
  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState<number>();
  const pickedSpecialists = useSelector(selectPickedSpecialists);

  const getSpecialistsState = specialistsApi.endpoints.getSpecialists.useQuery({ geo, pageNumber });
  const isFetching = getSpecialistsState.isFetching;
  const isError = getSpecialistsState.isError;
  const specialistsList = getSpecialistsState.data?.specialists as ISpecialistCatalogListing[];
  const specialistsPage = getSpecialistsState.data?.page;

  const pagination = useMemo(
    () => ({
      total: specialistsPage?.count || 0,
      limit: specialistsPage?.limit || 10,
      offset: specialistsPage?.offset || 0,
    }),
    [specialistsPage],
  );

  const selectedRealtorsId = useMemo(
    () => Object.keys(pickedSpecialists).map(index => Number(index)),
    [pickedSpecialists],
  );

  const handleSelectRealtor = useCallback(
    (item?: ISpecialistCatalogListing | null) => {
      if (item) {
        dispatch(addSpecialist(item));
        const amountSelectedRealtors = selectedRealtorsId.length + 1;
        trackSelectRealtor({ type: item.id, amountSelectedRealtors });
        trackMaxRealtors({ type: item.id, amountSelectedRealtors });
      }
    },
    [dispatch, selectedRealtorsId],
  );

  const handleUnselectRealtor = useCallback(
    (id: string) => {
      dispatch(removeSpecialist(id));
      const amountSelectedRealtors = selectedRealtorsId.length + 1;
      trackUnselectRealtor({ type: Number(id), amountSelectedRealtors });
    },
    [dispatch, selectedRealtorsId],
  );

  const handleSubmit = () => {
    setAnswersState(prevState => {
      const nextAnswersState: TAnswersState = {
        ...prevState,
        [answerField]: {
          answerField,
          value: selectedRealtorsId,
        },
      };

      setNextStep(nextAnswersState);
      trackSendForm(selectedRealtorsId);

      return nextAnswersState;
    });
  };

  return {
    selectedRealtorsId,
    handleSelectRealtor,
    handleUnselectRealtor,
    pagination,
    isFetching,
    isError,
    specialistsList,
    handleSubmit,
    setPageNumber,
    refetchGetSpecialists: getSpecialistsState.refetch,
  };
};
